import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { getApiConfig } from '@/settings/api';
import { ReducersTypes } from '@/redux/reducers/Models';

export interface CardBrand {
    codigoBandeira: string;
    descricaoBandeira: string;
}

const getCardBrands = async (useAutomaticApi: boolean): Promise<any> => {
  try {
    const { apiInstance } = getApiConfig(useAutomaticApi);
    const response = await apiInstance.get('/ff-dominios/v1/financeiro/bandeiras');
    return response?.data?.listaBandeiraCartao || [];
  } catch (error) {
    console.error('Erro ao buscar bandeiras de cartões:', error);
    throw new Error('Falha ao buscar bandeiras de cartões do servidor');
  }
};

export const useCardBrands = () => {
    const customer = useSelector((state: ReducersTypes) => state.customerReducer);
    
    const { useAutomaticApi, queryKey } = useMemo(() => {
        const hasCustomerToken = Boolean(customer?.accessToken);
        const useAutomaticApi = hasCustomerToken;
        
        return {
            useAutomaticApi,
            queryKey: ['bandeiras', useAutomaticApi]
        };
    }, [customer]);

    const { data: brands = [], isLoading, error, refetch } = useQuery<any>({
        queryKey,
        queryFn: () => getCardBrands(useAutomaticApi),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 2,
        refetchOnWindowFocus: false,
    });

    const formattedBrands: CardBrand[] = useMemo(() => 
        brands.map((brand: CardBrand) => ({
            descricaoBandeira: brand.descricaoBandeira?.toLowerCase(),
            codigoBandeira: brand.codigoBandeira,
        })), [brands]
    );
    
    return {
        brands: formattedBrands,
        isLoading,
        error,
        
        refetch,
    };
};

const filterBrand = (id: string, brands: CardBrand[]) => {
    return brands.find(brand => id === brand.codigoBandeira);
}

export const useSingleCardBrand = (id: string) => {
    const { brands, isLoading, error } = useCardBrands();
    const [brand, setBrand] = useState<CardBrand | undefined>(undefined);

    useEffect(() => {
        if(!isLoading && brands.length > 0 && id) {
            setBrand(filterBrand(id, brands));
        }
    }, [id, brands])
    
    return {
        brand,
        isLoading,
        error,
    };
};

